import { useTranslate } from "@refinedev/core";
import { ConfigProvider, Flex, Segmented } from "antd";
import { useAntTheme } from "hooks/useAntTheme";
import React, { useMemo } from "react";
import styled from "styled-components";
import { ActiveTabName } from "./types";

interface VoiceSelectorProps {
  custom: React.ReactNode;
  premade: React.ReactNode;
  activeKey: ActiveTabName;
  type: "face" | "voice";
  onTabClick?: (key: ActiveTabName) => void;
  renderVoiceFilters?: () => JSX.Element;
  filters?: React.ReactNode;
}

const StyledFlex = styled(Flex)`
  width: 100%;
  height: 100%;
`;

const VoiceAndFaceSelector = ({
  custom,
  premade,
  ...props
}: VoiceSelectorProps) => {
  const t = useTranslate();
  const [activeKey, setActiveKey] = React.useState<ActiveTabName>(null);
  const { theme, isMobile } = useAntTheme();

  const segmentedOptions = useMemo(
    () => [
      {
        label: t(
          "components.ContactWithPresenter.VoiceAndFaceSelector.presets"
        ),
        value: ActiveTabName.Premade,
      },
      {
        label: t(
          `components.ContactWithPresenter.VoiceAndFaceSelector.custom_${props.type}`
        ),
        value: ActiveTabName.Custom,
      },
    ],
    [props.type, t]
  );

  const handleChange = (value: ActiveTabName) => {
    props?.onTabClick?.(value);
    setActiveKey(value);
  };

  const currentActiveKey = activeKey ?? props.activeKey;

  return (
    <StyledFlex vertical style={{ width: "100%", height: "100%" }}>
      <Flex
        vertical={isMobile}
        align="center"
        gap={16}
        style={{ marginBottom: 16 }}
      >
        <ConfigProvider
          theme={{
            components: {
              Segmented: {
                itemColor: theme.colorTextDescription,
                fontSize: 16,
              },
            },
          }}
        >
          <StyledSegmented<ActiveTabName>
            isMobile={isMobile}
            value={currentActiveKey}
            onChange={handleChange}
            options={segmentedOptions}
          />
        </ConfigProvider>
        {currentActiveKey === ActiveTabName.Premade && props.filters && (
          <Flex style={{ flex: 1 }}>{props.filters}</Flex>
        )}
      </Flex>
      <Flex style={{ flex: 1, width: "100%" }}>
        {currentActiveKey === ActiveTabName.Premade ? premade : custom}
      </Flex>
    </StyledFlex>
  );
};

export default VoiceAndFaceSelector;

const StyledSegmented = styled(Segmented<ActiveTabName>)<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? "100%" : "50%")};
  padding: 10px;

  & .ant-segmented-item {
    flex: 1;
    text-align: center;
  }
`;
